/**
 * Cartridge selector — Hero + 9-tile Grid (sc-3125 design).
 *
 * Replaces the original vertical-list selector. With the catalog growing
 * to 25+ cartridges at launch, the list was scrolling off-screen and
 * had no surface to sell the focused cartridge (best score, daily
 * status, palette identity). The new layout splits the canvas:
 *
 *   ┌───────────────────────────┬─────────────────────────────┐
 *   │                           │  ┌───┐ ┌───┐ ┌───┐           │
 *   │   HERO PANEL              │  │   │ │   │ │   │           │
 *   │   (focused cartridge —    │  ├───┤ ├───┤ ├───┤           │
 *   │    title + palette +      │  │   │ │   │ │   │           │
 *   │    best + daily + CTA)    │  ├───┤ ├───┤ ├───┤           │
 *   │                           │  │   │ │   │ │   │           │
 *   │                           │  └───┘ └───┘ └───┘           │
 *   │                           │      ● ○ ○  PAGE 1/3         │
 *   └───────────────────────────┴─────────────────────────────┘
 *
 * Navigation:
 *   - arrows: move grid focus (2D, wraps within page)
 *   - Z / start: insert coin (launch focused cartridge)
 *   - X / C: previous / next page (the cabinet's fireB / fireC keys —
 *     Q/W were originally documented but Q is unbound and W maps to
 *     `up` in the WASD scheme, so neither could page)
 *
 * Async data:
 *   - YOUR BEST: fetched per-cartridge from /scores/:id on focus change,
 *     in-memory cached for the session
 *   - DAILY badge: fetched per-cartridge from /seeds/:id on focus change,
 *     same cache
 *   Both are best-effort — if the API is down the panel just shows "—".
 *
 * Determinism: the selector is NOT part of any replay. PRNG seeding
 * happens inside the launcher after the player picks.
 *
 * Resolution: runs at the cabinet baseline (480x270). The launcher
 * resizes the canvas to the cartridge's logical resolution after a pick
 * and restores it on exit, so the selector always sees the baseline.
 */

import type { GameInstance, GameModule } from '@arcraz/arcadia-types';
import type { InputSource } from './loop.js';
import { getLeaderboard, getDailySeed } from '../api/client.js';
import { isSignedIn } from '../auth/session.js';
import { present } from './compositor.js';
import { buildGameContext } from './context.js';
import { renderInto as renderToastsInto } from './toast.js';

/** Static seed used for every selector preview. The hero renders a single
 *  init() + render() pose (no update(), no PRNG draws beyond what init
 *  does), so the seed only affects the cartridge's first-frame layout —
 *  not any subsequent state. Using a constant keeps previews consistent
 *  across cabinet launches. */
const PREVIEW_SEED = 'arcadia84-selector-preview';

// --- Palette --------------------------------------------------------------
const SKY_TOP = '#0a0418';
const SKY_HORIZON = '#5a1a78';
const TITLE_YELLOW = '#ffea00';
const SUBTITLE_CYAN = '#00d9ff';
const HERO_BG = 'rgba(20, 6, 40, 0.55)';
const HERO_BORDER = '#5a1a78';
const HERO_BORDER_FOCUS = '#ffea00';
const TILE_BG = 'rgba(20, 6, 40, 0.55)';
const TILE_BG_FOCUS = 'rgba(60, 16, 90, 0.85)';
const TILE_BORDER = '#5a1a78';
const TILE_BORDER_FOCUS = '#ffea00';
const TILE_EMPTY = 'rgba(20, 6, 40, 0.25)';
const TILE_EMPTY_BORDER = 'rgba(90, 26, 120, 0.4)';
const STAT_LABEL = '#7af3c8';
const STAT_VALUE = '#ffffff';
const DAILY_BADGE = '#ff40c0';
const HINT_DIM = 'rgba(122, 243, 200, 0.5)';

// --- Layout ---------------------------------------------------------------
// Title is small + tight against the top so the hero/grid get more room.
const HERO_X = 8;
const HERO_Y = 18;
const HERO_W = 220;
const HERO_H = 232;

const GRID_X = 240;
const GRID_Y = 22;
const GRID_COLS = 3;
const GRID_ROWS = 3;
const GRID_TILE_W = 70;
const GRID_TILE_H = 56;
const GRID_GAP_X = 6;
const GRID_GAP_Y = 6;
const TILES_PER_PAGE = GRID_COLS * GRID_ROWS;

// --- Types ----------------------------------------------------------------
export interface SelectorResult {
  module_: GameModule;
}

interface CartridgeMeta {
  /** Personal best score, null = unknown, undefined = still loading. */
  best: number | null | undefined;
  /** True if a daily seed exists for today's UTC date. */
  hasDaily: boolean | undefined;
  /** Offscreen canvas with the cartridge's first-frame render, sized to
   *  the cartridge's native resolution. Null = render failed (cartridge
   *  threw during init/render), undefined = not yet rendered. */
  preview: HTMLCanvasElement | null | undefined;
}

const metaCache = new Map<string, CartridgeMeta>();

/**
 * Run the selector until the player picks a cartridge. Returns the chosen
 * GameModule. The selector is reactive: it polls input and re-renders at
 * 60Hz; metadata fetches happen async on focus change.
 */
export function runSelector(
  ctx2d: CanvasRenderingContext2D,
  input: InputSource,
  cartridges: readonly GameModule[]
): Promise<SelectorResult> {
  if (cartridges.length === 0) {
    return Promise.reject(new Error('Selector started with empty cartridge list.'));
  }

  let focusIndex = 0;
  let blink = 0;

  // Kick off meta fetches for the initially-focused cartridge.
  ensureMeta(cartridges[focusIndex] as GameModule);

  return new Promise<SelectorResult>((resolve) => {
    const tick = (): void => {
      const state = input.snapshot();
      const pageCount = Math.max(1, Math.ceil(cartridges.length / TILES_PER_PAGE));
      const currentPage = Math.floor(focusIndex / TILES_PER_PAGE);
      const pageStart = currentPage * TILES_PER_PAGE;
      const pageEnd = Math.min(pageStart + TILES_PER_PAGE, cartridges.length);
      const slot = focusIndex - pageStart; // 0..8
      const col = slot % GRID_COLS;
      const row = Math.floor(slot / GRID_COLS);

      // Page flip via Q (fireC) / W (fireB). The cabinet keymap maps Q→fireC
      // and W→fireB by default; if the keymap shifts these later, the
      // selector still has start/fireA for insert-coin which is the
      // critical input.
      let nextFocus = focusIndex;
      if (state.justPressed.has('fireC') && pageCount > 1) {
        // Previous page, wrap to last.
        const targetPage = (currentPage - 1 + pageCount) % pageCount;
        nextFocus = targetPage * TILES_PER_PAGE;
      } else if (state.justPressed.has('fireB') && pageCount > 1) {
        // Next page, wrap to first.
        const targetPage = (currentPage + 1) % pageCount;
        nextFocus = targetPage * TILES_PER_PAGE;
      } else if (state.justPressed.has('up')) {
        if (row > 0) nextFocus = pageStart + (row - 1) * GRID_COLS + col;
      } else if (state.justPressed.has('down')) {
        const candidate = pageStart + (row + 1) * GRID_COLS + col;
        if (row < GRID_ROWS - 1 && candidate < pageEnd) nextFocus = candidate;
      } else if (state.justPressed.has('left')) {
        if (col > 0) nextFocus = pageStart + row * GRID_COLS + (col - 1);
      } else if (state.justPressed.has('right')) {
        const candidate = pageStart + row * GRID_COLS + (col + 1);
        if (col < GRID_COLS - 1 && candidate < pageEnd) nextFocus = candidate;
      }

      if (nextFocus !== focusIndex) {
        focusIndex = nextFocus;
        ensureMeta(cartridges[focusIndex] as GameModule);
      }

      if (state.justPressed.has('start') || state.justPressed.has('fireA')) {
        const picked = cartridges[focusIndex] as GameModule;
        resolve({ module_: picked });
        return;
      }

      // Blink cycle: 120 frames @ 60Hz = 2s. Slow pulse for the PRESS Z
      // CTA — fast strobes read as broken; this reads as "waiting for you".
      blink = (blink + 1) % 120;
      render(ctx2d, cartridges, focusIndex, blink);
      renderToastsInto(ctx2d);
      present();
      requestAnimationFrame(tick);
    };

    tick();
  });
}

// --- Metadata fetching ----------------------------------------------------

/**
 * Lazily populate cache for the focused cartridge. Best-effort — failures
 * leave the slot as `null` so the UI renders "—" instead of spinning.
 * Subsequent focus visits short-circuit on cache hit.
 */
function ensureMeta(module_: GameModule): void {
  if (metaCache.has(module_.id)) return;
  const meta: CartridgeMeta = {
    best: undefined,
    hasDaily: undefined,
    preview: undefined,
  };
  metaCache.set(module_.id, meta);

  // Render a frozen first-frame preview into an offscreen canvas at the
  // cartridge's native resolution. The hero panel later draws this scaled
  // to fit. We call init() once + render() once — never update() — so
  // PRNG state past whatever the cartridge consumes in its constructor
  // doesn't bleed into anything player-visible (this is the same
  // contract runPressStart uses for the press-to-start gate).
  meta.preview = renderPreview(module_);

  // Personal best: fetch the top-N for this cartridge; the API returns
  // `me.playerId` for signed-in callers — find the player's highest row.
  // For anonymous players, `me` is null and we leave best as null.
  void (async (): Promise<void> => {
    try {
      const lb = await getLeaderboard(module_.id, { period: 'all', limit: 100 });
      if (!lb.me) {
        meta.best = null;
        return;
      }
      let highest = 0;
      for (const entry of lb.scores) {
        if (entry.playerId === lb.me.playerId && entry.score > highest) {
          highest = entry.score;
        }
      }
      meta.best = highest > 0 ? highest : null;
    } catch {
      meta.best = null;
    }
  })();

  // Daily seed: presence is the signal. 404 means no seed scheduled
  // (or unknown cartridge), which we render as "—".
  void (async (): Promise<void> => {
    try {
      await getDailySeed(module_.id);
      meta.hasDaily = true;
    } catch {
      meta.hasDaily = false;
    }
  })();
}

// --- Preview rendering ----------------------------------------------------

/**
 * Render a cartridge's first frame into an offscreen canvas at its native
 * resolution. Returns the canvas, or null if the cartridge threw during
 * construction / render. Failure is non-fatal — the hero panel falls
 * back to the stylized palette card.
 *
 * We skip module_.preload() because it only registers SFX (cartridge
 * audio doesn't paint pixels) and the selector is not the right place
 * to spam the audio bus's sample registry. This is documented in
 * GameModule.preload as "register audio assets" — pure audio side
 * effect. If a cartridge ever expects preload to load *visual* assets
 * (sprites, fonts), the contract will need a separate hook; today it
 * doesn't.
 */
function renderPreview(module_: GameModule): HTMLCanvasElement | null {
  try {
    const off = document.createElement('canvas');
    off.width = module_.resolution.w;
    off.height = module_.resolution.h;
    const og = off.getContext('2d');
    if (!og) return null;
    og.imageSmoothingEnabled = !module_.pixelated;

    const previewCtx = buildGameContext({
      seed: PREVIEW_SEED,
      onExit: () => {
        // Cartridge can't exit a preview — onExit is a no-op. The
        // selector controls focus + launch entirely.
      },
    });
    const instance: GameInstance = module_.init(previewCtx, {
      seed: PREVIEW_SEED,
      mode: 'arcade',
    });
    instance.render(og);
    return off;
  } catch {
    // Cartridge bug shouldn't bring the selector down. Log once via the
    // browser console (the dev cabinet is open in devtools); production
    // silently falls back to the palette card.
    console.warn(`[selector] preview render failed for ${module_.id}`);
    return null;
  }
}

// --- Render ---------------------------------------------------------------

function render(
  g: CanvasRenderingContext2D,
  cartridges: readonly GameModule[],
  focusIndex: number,
  blink: number
): void {
  const WIDTH = g.canvas.width;
  const HEIGHT = g.canvas.height;

  // Background gradient — same vaporwave wash the original used.
  const grad = g.createLinearGradient(0, 0, 0, HEIGHT);
  grad.addColorStop(0, SKY_TOP);
  grad.addColorStop(0.7, SKY_HORIZON);
  grad.addColorStop(1, SKY_TOP);
  g.fillStyle = grad;
  g.fillRect(0, 0, WIDTH, HEIGHT);

  // Title strip — kept small + tight so the hero/grid below get the
  // real estate. Cabinet marquee already reads "ARCADIA '84" so this is
  // a quiet sub-label, not a hero.
  g.textAlign = 'center';
  g.textBaseline = 'top';
  g.fillStyle = TITLE_YELLOW;
  g.font = 'bold 7px ui-monospace, monospace';
  g.fillText('SELECT YOUR GAME', WIDTH / 2, 6);

  const focused = cartridges[focusIndex] as GameModule;
  renderHero(g, focused, blink);
  renderGrid(g, cartridges, focusIndex);
  // Reset defaults — other render paths assume these.
  g.textAlign = 'left';
  g.textBaseline = 'top';
}

function renderHero(
  g: CanvasRenderingContext2D,
  m: GameModule,
  blink: number
): void {
  // Outer hero panel — bordered "now-showing" surface.
  g.fillStyle = HERO_BG;
  g.fillRect(HERO_X, HERO_Y, HERO_W, HERO_H);
  g.strokeStyle = HERO_BORDER_FOCUS;
  g.lineWidth = 1;
  g.strokeRect(HERO_X + 0.5, HERO_Y + 0.5, HERO_W - 1, HERO_H - 1);

  // Mini "marquee" preview at the top of the hero — a stylized mini-bezel
  // showing the cartridge's palette as a vector composition. We don't
  // run the cartridge's render() here (would need an offscreen canvas
  // at the cartridge's native resolution); instead, build a tinted
  // identity card from paletteHint that conveys aesthetic at a glance.
  const previewX = HERO_X + 8;
  const previewY = HERO_Y + 8;
  const previewW = HERO_W - 16;
  const previewH = 80;
  renderHeroPreview(g, m, previewX, previewY, previewW, previewH);

  // Title in accent color.
  const accent = m.paletteHint[0] ?? TITLE_YELLOW;
  let cursorY = previewY + previewH + 10;
  g.textAlign = 'center';
  g.fillStyle = accent;
  g.font = 'bold 16px ui-monospace, monospace';
  g.fillText(m.title, HERO_X + HERO_W / 2, cursorY);
  cursorY += 18;

  // Palette dot row + genre/year.
  renderPaletteDots(g, m, HERO_X + HERO_W / 2, cursorY);
  cursorY += 10;
  g.fillStyle = SUBTITLE_CYAN;
  g.font = '8px ui-monospace, monospace';
  g.fillText(`${m.genre.toUpperCase()} · ${m.year}`, HERO_X + HERO_W / 2, cursorY);
  cursorY += 14;

  // Stats — best score + daily badge.
  const meta = metaCache.get(m.id);
  const bestLabel = formatBest(meta);
  const dailyLabel = formatDaily(meta);

  g.textAlign = 'left';
  g.fillStyle = STAT_LABEL;
  g.font = '8px ui-monospace, monospace';
  g.fillText('YOUR BEST', HERO_X + 16, cursorY);
  g.textAlign = 'right';
  g.fillStyle = STAT_VALUE;
  g.fillText(bestLabel, HERO_X + HERO_W - 16, cursorY);
  cursorY += 11;

  g.textAlign = 'left';
  g.fillStyle = STAT_LABEL;
  g.fillText('DAILY', HERO_X + 16, cursorY);
  g.textAlign = 'right';
  g.fillStyle = meta?.hasDaily ? DAILY_BADGE : STAT_VALUE;
  g.fillText(dailyLabel, HERO_X + HERO_W - 16, cursorY);
  cursorY += 14;

  if (!isSignedIn() && meta?.best === null) {
    g.textAlign = 'center';
    g.fillStyle = HINT_DIM;
    g.font = '7px ui-monospace, monospace';
    g.fillText('(SIGN IN TO TRACK)', HERO_X + HERO_W / 2, cursorY);
    cursorY += 10;
  }

  // CTA — slow blink (~2s cycle, on 75% of the time). Reads as a gentle
  // attract pulse rather than a strobe.
  if (blink < 90) {
    g.textAlign = 'center';
    g.fillStyle = TITLE_YELLOW;
    g.font = 'bold 10px ui-monospace, monospace';
    g.fillText('▶ PRESS Z', HERO_X + HERO_W / 2, HERO_Y + HERO_H - 18);
  }
}

/**
 * "Now showing" identity card. Top quarter is a palette-tinted marquee
 * band with the title — same energy as the cabinet's physical marquee.
 * The remaining playfield area shows the cartridge's actual rendered
 * first frame (init + render once into an offscreen canvas at the
 * cartridge's native resolution, then drawImage()'d here scaled to
 * fit). If preview render failed (cartridge bug, or still loading),
 * falls back to a stylized stroke pattern derived from the cartridge id.
 */
function renderHeroPreview(
  g: CanvasRenderingContext2D,
  m: GameModule,
  x: number,
  y: number,
  w: number,
  h: number
): void {
  const accent = m.paletteHint[0] ?? TITLE_YELLOW;

  // Playfield base — dark wash so any letterboxing around the preview
  // canvas matches the cabinet sky.
  g.fillStyle = '#06040c';
  g.fillRect(x, y, w, h);

  // Marquee band — top quarter, tinted to the accent.
  const bandH = Math.floor(h / 4);
  const bandGrad = g.createLinearGradient(x, y, x, y + bandH);
  bandGrad.addColorStop(0, accent);
  bandGrad.addColorStop(1, withAlpha(accent, 0.4));
  g.fillStyle = bandGrad;
  g.fillRect(x, y, w, bandH);

  // Marquee text — tiny title across the band, monochrome dark for
  // contrast against the bright band.
  g.fillStyle = '#06040c';
  g.font = 'bold 9px ui-monospace, monospace';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(m.title, x + w / 2, y + bandH / 2);

  // Playfield zone — below the marquee band.
  const playY = y + bandH + 2;
  const playH = h - bandH - 2;

  const meta = metaCache.get(m.id);
  const preview = meta?.preview;
  if (preview) {
    // Draw the cartridge's actual first-frame render. The cartridge's
    // native resolution may be 480x270 OR 960x540; we letterbox to fit
    // the play area while preserving aspect (16:9 for both, but be
    // safe in case a future cartridge declares something else).
    const srcW = preview.width;
    const srcH = preview.height;
    const srcAR = srcW / srcH;
    const dstAR = w / playH;
    let drawW = w;
    let drawH = playH;
    if (srcAR > dstAR) {
      // Source wider than destination — letterbox top/bottom.
      drawH = w / srcAR;
    } else {
      drawW = playH * srcAR;
    }
    const drawX = x + (w - drawW) / 2;
    const drawY = playY + (playH - drawH) / 2;
    // Pixel cartridges want crisp-edges scaling; vector cartridges want
    // smoothing on the downscale. The 2d context's imageSmoothingEnabled
    // is per-context, so we toggle it for the duration of the draw.
    const prevSmoothing = g.imageSmoothingEnabled;
    g.imageSmoothingEnabled = !m.pixelated;
    g.drawImage(preview, drawX, drawY, drawW, drawH);
    g.imageSmoothingEnabled = prevSmoothing;
  } else {
    // Fallback — three diagonal accent strokes whose angles vary by id.
    // Same visual identity-card pattern the MVP used.
    const accent2 = m.paletteHint[1] ?? SUBTITLE_CYAN;
    const accent3 = m.paletteHint[2] ?? STAT_LABEL;
    const accent4 = m.paletteHint[3] ?? accent;
    const seed = hashId(m.id);
    g.lineWidth = 1.5;
    for (let i = 0; i < 3; i++) {
      const t = ((seed >> (i * 3)) & 0b111) / 7;
      const color = [accent2, accent3, accent4][i] ?? accent;
      g.strokeStyle = color;
      g.beginPath();
      g.moveTo(x + 4 + t * (w - 8), playY + 2);
      g.lineTo(x + w - 4 - (1 - t) * (w - 8), playY + playH - 2);
      g.stroke();
    }
  }

  // Outline.
  g.strokeStyle = HERO_BORDER;
  g.lineWidth = 1;
  g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

  // Reset for callers downstream.
  g.textBaseline = 'top';
}

function renderPaletteDots(
  g: CanvasRenderingContext2D,
  m: GameModule,
  centerX: number,
  y: number
): void {
  const dotW = 6;
  const dotGap = 3;
  const dots = Math.min(m.paletteHint.length, 4);
  const totalW = dots * dotW + (dots - 1) * dotGap;
  const x0 = centerX - totalW / 2;
  for (let i = 0; i < dots; i++) {
    g.fillStyle = m.paletteHint[i] as string;
    g.fillRect(x0 + i * (dotW + dotGap), y, dotW, dotW);
  }
}

function renderGrid(
  g: CanvasRenderingContext2D,
  cartridges: readonly GameModule[],
  focusIndex: number
): void {
  const pageCount = Math.max(1, Math.ceil(cartridges.length / TILES_PER_PAGE));
  const currentPage = Math.floor(focusIndex / TILES_PER_PAGE);
  const pageStart = currentPage * TILES_PER_PAGE;

  for (let slot = 0; slot < TILES_PER_PAGE; slot++) {
    const col = slot % GRID_COLS;
    const row = Math.floor(slot / GRID_COLS);
    const x = GRID_X + col * (GRID_TILE_W + GRID_GAP_X);
    const y = GRID_Y + row * (GRID_TILE_H + GRID_GAP_Y);
    const idx = pageStart + slot;
    const m = cartridges[idx];

    if (!m) {
      // Empty slot on partial last page — dimmed frame so the grid
      // rhythm stays constant.
      g.fillStyle = TILE_EMPTY;
      g.fillRect(x, y, GRID_TILE_W, GRID_TILE_H);
      g.strokeStyle = TILE_EMPTY_BORDER;
      g.lineWidth = 1;
      g.strokeRect(x + 0.5, y + 0.5, GRID_TILE_W - 1, GRID_TILE_H - 1);
      continue;
    }

    // Warm the preview for every visible tile (not just focused) so the
    // grid shows real cartridge frames, not placeholders. init() is sync
    // and cheap — same cost as a single render frame of the cartridge.
    ensureMeta(m);

    const focused = idx === focusIndex;
    renderTile(g, m, x, y, focused);
  }

  // Page indicator dots — only visible if there's more than one page.
  if (pageCount > 1) {
    const dotR = 2;
    const dotGap = 6;
    const totalW = pageCount * dotR * 2 + (pageCount - 1) * dotGap;
    const dotsX = GRID_X + ((GRID_TILE_W + GRID_GAP_X) * GRID_COLS - GRID_GAP_X) / 2 - totalW / 2;
    const dotsY = GRID_Y + GRID_ROWS * (GRID_TILE_H + GRID_GAP_Y);
    for (let p = 0; p < pageCount; p++) {
      g.fillStyle = p === currentPage ? TITLE_YELLOW : 'rgba(255,234,0,0.25)';
      g.beginPath();
      g.arc(dotsX + p * (dotR * 2 + dotGap) + dotR, dotsY + dotR, dotR, 0, Math.PI * 2);
      g.fill();
    }

    g.fillStyle = HINT_DIM;
    g.font = '7px ui-monospace, monospace';
    g.textAlign = 'right';
    g.fillText(
      `PAGE ${currentPage + 1}/${pageCount}`,
      GRID_X + (GRID_TILE_W + GRID_GAP_X) * GRID_COLS - GRID_GAP_X,
      dotsY - 1
    );
    g.textAlign = 'left';
  }

  // Controls hint sits under the grid (replaces the canvas-wide footer).
  // Centered across the grid columns; renders every frame (no blink).
  const hintCenterX =
    GRID_X + ((GRID_TILE_W + GRID_GAP_X) * GRID_COLS - GRID_GAP_X) / 2;
  const hintY =
    GRID_Y + GRID_ROWS * (GRID_TILE_H + GRID_GAP_Y) + (pageCount > 1 ? 12 : 4);
  g.textAlign = 'center';
  g.textBaseline = 'top';
  g.font = '7px ui-monospace, monospace';
  g.fillStyle = HINT_DIM;
  g.fillText(
    'ARROWS · NAV    Z · INSERT COIN    X / C · PAGE',
    hintCenterX,
    hintY
  );
  g.textAlign = 'left';
}

function renderTile(
  g: CanvasRenderingContext2D,
  m: GameModule,
  x: number,
  y: number,
  focused: boolean
): void {
  const accent = m.paletteHint[0] ?? TITLE_YELLOW;

  // Tile body.
  g.fillStyle = focused ? TILE_BG_FOCUS : TILE_BG;
  g.fillRect(x, y, GRID_TILE_W, GRID_TILE_H);
  g.strokeStyle = focused ? TILE_BORDER_FOCUS : TILE_BORDER;
  g.lineWidth = focused ? 2 : 1;
  g.strokeRect(x + 0.5, y + 0.5, GRID_TILE_W - 1, GRID_TILE_H - 1);

  // Accent band on the top edge — tiny tinted marquee strip, evokes the
  // cabinet's lighted top frame.
  g.fillStyle = accent;
  g.fillRect(x + 2, y + 2, GRID_TILE_W - 4, 4);

  // Preview "screen" — the cartridge's actual rendered first frame,
  // letterboxed into the playfield area of the tile. Falls back to a
  // faint title if preview render failed.
  const screenX = x + 4;
  const screenY = y + 8;
  const screenW = GRID_TILE_W - 8;
  const screenH = GRID_TILE_H - 22; // leave room for title strip below
  g.fillStyle = '#06040c';
  g.fillRect(screenX, screenY, screenW, screenH);

  const meta = metaCache.get(m.id);
  const preview = meta?.preview;
  if (preview) {
    const srcW = preview.width;
    const srcH = preview.height;
    const srcAR = srcW / srcH;
    const dstAR = screenW / screenH;
    let drawW = screenW;
    let drawH = screenH;
    if (srcAR > dstAR) {
      drawH = screenW / srcAR;
    } else {
      drawW = screenH * srcAR;
    }
    const drawX = screenX + (screenW - drawW) / 2;
    const drawY = screenY + (screenH - drawH) / 2;
    const prevSmoothing = g.imageSmoothingEnabled;
    g.imageSmoothingEnabled = !m.pixelated;
    g.drawImage(preview, drawX, drawY, drawW, drawH);
    g.imageSmoothingEnabled = prevSmoothing;
  } else {
    // Fallback — show full title in the screen area, wrapped/shrunk to fit.
    g.fillStyle = 'rgba(255,234,0,0.6)';
    renderTitleFitting(
      g,
      m.title,
      screenX + screenW / 2,
      screenY + screenH / 2,
      screenW - 4,
      8
    );
  }

  // Title strip below the screen — bottom band of the tile.
  g.fillStyle = focused ? TITLE_YELLOW : 'rgba(255,234,0,0.6)';
  renderTitleFitting(
    g,
    m.title,
    x + GRID_TILE_W / 2,
    y + GRID_TILE_H - 8,
    GRID_TILE_W - 4,
    7,
    focused
  );

  // Daily-seed dot in the top-right corner if available.
  if (meta?.hasDaily) {
    g.fillStyle = DAILY_BADGE;
    g.beginPath();
    g.arc(x + GRID_TILE_W - 6, y + 6, 2, 0, Math.PI * 2);
    g.fill();
  }

  g.textBaseline = 'top';
}

// --- Helpers --------------------------------------------------------------

function formatBest(meta: CartridgeMeta | undefined): string {
  if (!meta) return '—';
  if (meta.best === undefined) return '…';
  if (meta.best === null || meta.best === 0) return '—';
  return meta.best.toLocaleString('en-US');
}

function formatDaily(meta: CartridgeMeta | undefined): string {
  if (!meta) return '—';
  if (meta.hasDaily === undefined) return '…';
  return meta.hasDaily ? '◆ NEW SEED' : '—';
}

/** Add an alpha channel to a hex color. Falls through if not parseable. */
function withAlpha(hex: string, alpha: number): string {
  if (!hex.startsWith('#') || (hex.length !== 7 && hex.length !== 4)) {
    return hex;
  }
  let r = 0;
  let g = 0;
  let b = 0;
  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  } else {
    const c1 = hex.charAt(1);
    const c2 = hex.charAt(2);
    const c3 = hex.charAt(3);
    r = parseInt(c1 + c1, 16);
    g = parseInt(c2 + c2, 16);
    b = parseInt(c3 + c3, 16);
  }
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Render a cartridge title centered at (cx, cy), fitting within maxW.
 *
 * Strategy:
 *   1. Try the full title on one line at the requested font size.
 *   2. If it overflows, wrap on the first space (most titles are 2 words):
 *      "NEON OVERPASS" → two centered lines.
 *   3. If the longest single word still overflows, shrink the font size
 *      until it fits.
 *
 * Both render paths use `textAlign='center', textBaseline='middle'`, then
 * restore baseline to 'top' (the selector's outer default).
 */
function renderTitleFitting(
  g: CanvasRenderingContext2D,
  title: string,
  cx: number,
  cy: number,
  maxW: number,
  basePx: number,
  bold = false
): void {
  const setFont = (px: number): void => {
    g.font = `${bold ? 'bold ' : ''}${px}px ui-monospace, monospace`;
  };

  g.textAlign = 'center';
  g.textBaseline = 'middle';

  setFont(basePx);
  if (g.measureText(title).width <= maxW) {
    g.fillText(title, cx, cy);
    g.textBaseline = 'top';
    return;
  }

  const spaceIdx = title.indexOf(' ');
  if (spaceIdx > 0) {
    const lineA = title.slice(0, spaceIdx);
    const lineB = title.slice(spaceIdx + 1);
    // Slightly smaller font for two-line wrap so the pair fits the tile
    // height without crowding the preview thumbnail above.
    const wrapPx = Math.max(5, basePx - 1);
    setFont(wrapPx);
    if (
      g.measureText(lineA).width <= maxW &&
      g.measureText(lineB).width <= maxW
    ) {
      const lineH = wrapPx + 1;
      g.fillText(lineA, cx, cy - lineH / 2);
      g.fillText(lineB, cx, cy + lineH / 2);
      g.textBaseline = 'top';
      return;
    }
  }

  // Last resort — shrink font until the title fits on one line. Bounded
  // by a 4px floor so we never render unreadable specks.
  let px = basePx;
  while (px > 4 && g.measureText(title).width > maxW) {
    px -= 1;
    setFont(px);
  }
  g.fillText(title, cx, cy);
  g.textBaseline = 'top';
}

/** Tiny deterministic hash of a cartridge id → 31-bit unsigned. Used to
 *  derive the stylized stroke angles in the hero palette card so each
 *  cartridge has its own visual fingerprint. Not security-relevant. */
function hashId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h;
}
